import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { webApi, type ApiUser } from '@/services/webApi'

export interface User {
  id: number
  name: string
  email?: string
  mobile: string
  image?: string | null
  isOtpVerified: boolean
}

interface AuthState {
  token: string | null
  user: User | null
  isAuthenticated: boolean
  setAuth: (token: string, user: User) => void
  clearSession: () => void
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

export function mapApiUser(u: ApiUser): User {
  return {
    id: u.id,
    name: u.name?.trim() || 'QBC User',
    email: u.email || undefined,
    mobile: u.mobile,
    image: u.image,
    isOtpVerified: Boolean(u.isOtpVerified),
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      setAuth: (token, user) => set({ token, user, isAuthenticated: true }),
      clearSession: () => set({ token: null, user: null, isAuthenticated: false }),
      logout: async () => {
        const { token } = get()
        if (token) {
          try {
            await webApi.logout()
          } catch {
            // Clear local session even if the server call fails.
          }
        }
        get().clearSession()
      },
      refreshUser: async () => {
        const { token } = get()
        if (!token) return
        try {
          const user = await webApi.me()
          set({ user: mapApiUser(user), isAuthenticated: true })
        } catch {
          get().clearSession()
        }
      },
    }),
    { name: 'qbc-auth' },
  ),
)
